import * as React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { XL_COLUMN, XXL_COLUMN } from "../utils/columnSizes";
import {
  useCreateTeam,
  useDeleteTeam,
  useGetTeam,
  useTeams,
  useUpdateTeam,
} from "../requests/sendGetTeams";
import { Team } from "../requests/utils/databaseTypes";
import { isValidHexColor } from "../utils/isValidHexColor";
import { CustomSpinner } from "../components/CustomSpinner";
import { useMatches } from "../requests/sendGetMatches";
import { MutableGridCol } from "../components/CrudTable/columns/MutableGridCol";
import { NewColorPickerGridCol } from "../components/CrudTable/columns/ColorPickerGridCol";
import { FullFeaturedCrudGrid } from "../components/CrudTable/FullFeaturedCrudGrid";

const isValidTeam = (team?: Team) =>
  team && team.name.trim().length > 0 && isValidHexColor(team.color.trim());

export const Teams = () => {
  const teams = useTeams();
  const getTeam = useGetTeam();
  const createTeam = useCreateTeam().mutateAsync;
  const updateTeam = useUpdateTeam().mutate;
  const deleteTeam = useDeleteTeam().mutate;

  const matches = useMatches();

  if (!teams || !matches) {
    return <CustomSpinner />;
  }

  const isMatchAssociatedWithTeam = (teamId: number): boolean =>
    matches
      .map((match) => match.home_team_id)
      .concat(matches.map((match) => match.away_team_id))
      .includes(teamId);

  const columns: GridColDef[] = [
    new MutableGridCol("name", "Team", XXL_COLUMN),
    new NewColorPickerGridCol<Team>("color", "Color", XL_COLUMN),
  ];

  const getNewRow = (id: number): Team => ({ id, name: "", color: "#000000" });

  const getDeleteDialog = (id: number | null) => {
    const team = getTeam(id);
    return `Delete ${team?.name}?`;
  };

  return (
    <FullFeaturedCrudGrid
      crudRequests={{
        useCollection: useTeams,
        postSingleton: (team: Team) => createTeam(team),
        putSingleton: (team: Team) => updateTeam(team),
        deleteSingleton: (id: number) => deleteTeam(id),
      }}
      validators={{
        isValidCreate: isValidTeam,
        isValidDelete: (team: Team) => !isMatchAssociatedWithTeam(team.id),
      }}
      columns={columns}
      getNewRow={getNewRow}
      getDeleteDialog={getDeleteDialog}
      unableToDeleteTooltip="The team can not be deleted while associated with a match."
      itemName="team"
      initialSortModel={[{ field: "name", sort: "asc" }]}
      fieldToFocus="name"
    />
  );
};
